/**
 * @file EntityLink.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-20
 * @modified 2026-08-20
 * @description Makes any displayed entity clickable through to its own modal.
 *
 *              <EntityLink type="invoice" id={inv.id}>{inv.invoice_no}</EntityLink>
 *
 *              Renders a real <button>, so it is keyboard reachable and screen
 *              readers announce it as actionable — a clickable <span> is
 *              neither. With no id it renders the children as plain text rather
 *              than a dead control, so a missing link never looks clickable.
 */

import React from 'react';
import './EntityLink.css';
import { useEntityModal, ENTITY_REGISTRY } from './EntityModalProvider';

function EntityLink({
  type,
  id,
  children,
  className = '',
  title,
  variant = 'inline',   // 'inline' | 'badge'
  stopPropagation = true,
  ...rest
}) {
  const { openEntity, available } = useEntityModal();
  const entry = ENTITY_REGISTRY[type];

  // No id, unknown type, or no provider above us — show the text, not a control.
  if (!id || !entry || !available) {
    return <span className={className} {...rest}>{children}</span>;
  }

  const handleClick = (e) => {
    // Entity links very often sit inside an expandable/selectable table row.
    // Without this, opening the modal also toggles the row underneath it.
    if (stopPropagation) { e.stopPropagation(); e.preventDefault(); }
    openEntity(type, id);
  };

  return (
    <button
      type="button"
      className={`entity-link entity-link-${variant} ${className}`.trim()}
      onClick={handleClick}
      title={title || `View ${entry.label.toLowerCase()}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export default EntityLink;
